import { captureMessage } from '@sentry/browser';
import React from 'react';
import { getParameterByName } from './utils/getParameterByName';

export interface IPlayerState {
    assetId?: undefined | string;
    useHighBitrate: boolean;
    isSlowMotion: boolean;
}

export class Player extends React.Component<any, IPlayerState> {
    private videoElementHighRef: React.RefObject<HTMLVideoElement>;
    private videoElementLowRef: React.RefObject<HTMLVideoElement>;

    constructor(props: any) {
        super(props);
        this.videoElementHighRef = React.createRef<HTMLVideoElement>();
        this.videoElementLowRef = React.createRef<HTMLVideoElement>();

        this.state = {
            useHighBitrate: true,
            isSlowMotion: false,
            assetId: getParameterByName('assetid'),
        };
    }

    public override render() {
        if (!this.state.assetId) {
            return <span />;
        }

        return (
            <div style={{ height: '100vh' }}>
                <div className="admin-form mb10" style={{ height: '100vh' }}>
                    <div className="section p10" style={{ height: '100vh' }}>
                        <video
                            ref={this.videoElementLowRef}
                            id={'videolow'}
                            className={this.state.useHighBitrate ? 'hidden' : ''}
                            crossOrigin="use-credentials"
                            preload="auto"
                            controls={true}
                            height="85%"
                            muted
                        >
                            <source src={`/api/assets/${this.state.assetId}/download?downscaled=true`} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <video
                            ref={this.videoElementHighRef}
                            id={'videohigh'}
                            className={this.state.useHighBitrate ? '' : 'hidden'}
                            crossOrigin="use-credentials"
                            preload="metadata"
                            controls={true}
                            height="85%"
                            muted
                            onLoadedData={(e) => {
                                e.currentTarget.focus();
                            }}
                        >
                            <source src={`/api/assets/${this.state.assetId}/download?downscaledHigh=true`} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <div className="mt20">
                            <button
                                className={this.state.isSlowMotion ? 'hidden' : 'btn btn-default btn-sm mr10 fs12'}
                                onClick={() => this.onPlaybackRateChanged(0.5)}
                            >
                                {' '}
                                <span className="fa fa-clock-o pr5"></span>Slowmotion
                            </button>
                            <button
                                className={this.state.isSlowMotion ? 'btn btn-success btn-sm mr10 fs12' : 'hidden'}
                                onClick={() => this.onPlaybackRateChanged(1)}
                            >
                                {' '}
                                <span className="fa fa-clock-o pr5"></span> Slowmotion
                            </button>
                            <button
                                className={this.state.useHighBitrate ? 'hidden' : 'btn btn-default btn-sm mr10 fs12'}
                                onClick={() => this.handleBitRate()}
                            >
                                {' '}
                                <span className="fa fa-star-half-full pr5"></span>High quality
                            </button>
                            <button
                                className={this.state.useHighBitrate ? 'btn btn btn-success btn-sm mr10 fs12' : 'hidden'}
                                onClick={() => this.handleBitRate()}
                            >
                                {' '}
                                <span className="fa fa-star pr5"></span> High quality
                            </button>
                            <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onFrameByFrame('backward')}>
                                {' '}
                                <span className="fa fa-step-backward"></span>
                            </button>
                            <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onFrameByFrame('forward')}>
                                {' '}
                                <span className="fa fa-step-forward"></span>
                            </button>
                            <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetBack(1)}>
                                {' '}
                                <span className="fa fa-undo pr5"></span>1
                            </button>
                            <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetBack(5)}>
                                {' '}
                                <span className="fa fa-undo pr5"></span>5
                            </button>
                            <a
                                className="btn btn-primary btn-sm mr10 fs12"
                                href={`/api/assets/${this.state.assetId}/download?downscaledHigh=true&forceDownload=true`}
                                download
                            >
                                {' '}
                                <span className="fa fa-download pr5"></span>Download
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handleBitRate() {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }

        if (this.state.useHighBitrate) {
            this.videoElementLowRef.current.pause();
            this.videoElementHighRef.current.currentTime = this.videoElementLowRef.current.currentTime;
            this.setState({ useHighBitrate: false });
        } else {
            captureMessage('Using low bitrate.');
            this.videoElementHighRef.current.pause();
            this.videoElementLowRef.current.currentTime = this.videoElementHighRef.current.currentTime;
            this.setState({ useHighBitrate: true });
        }
    }

    private onFrameByFrame(direction: string) {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }
        const frameTime = 1 / 30;

        if (!this.videoElementHighRef.current.paused) {
            this.videoElementHighRef.current.pause();
        }
        if (!this.videoElementLowRef.current.paused) {
            this.videoElementLowRef.current.pause();
        }

        if (direction === 'forward') {
            if (this.videoElementHighRef.current.currentTime < this.videoElementHighRef.current.duration) {
                if (this.state.useHighBitrate) {
                    this.videoElementHighRef.current.currentTime = Math.min(
                        this.videoElementHighRef.current.duration,
                        this.videoElementHighRef.current.currentTime + frameTime,
                    );
                    this.videoElementLowRef.current.currentTime = Math.min(
                        this.videoElementHighRef.current.duration,
                        this.videoElementHighRef.current.currentTime + frameTime,
                    );
                } else {
                    this.videoElementHighRef.current.currentTime = Math.min(
                        this.videoElementLowRef.current.duration,
                        this.videoElementLowRef.current.currentTime + frameTime,
                    );
                    this.videoElementLowRef.current.currentTime = Math.min(
                        this.videoElementLowRef.current.duration,
                        this.videoElementLowRef.current.currentTime + frameTime,
                    );
                }
            }
        } else {
            if (this.videoElementHighRef.current.currentTime > 0) {
                this.videoElementHighRef.current.currentTime -= frameTime;
                this.videoElementLowRef.current.currentTime -= frameTime;
            }
        }
    }

    private onVideoSetBack(value: number) {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }

        if (this.videoElementHighRef.current.currentTime - value > 0) {
            this.videoElementHighRef.current.currentTime = this.videoElementHighRef.current.currentTime - value;
            this.videoElementLowRef.current.currentTime = this.videoElementLowRef.current.currentTime - value;
        } else {
            this.videoElementHighRef.current.currentTime = 0;
            this.videoElementLowRef.current.currentTime = 0;
        }
    }

    private onPlaybackRateChanged(value: number) {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }

        if (value === 1) {
            this.setState({ isSlowMotion: false });
        } else {
            this.setState({ isSlowMotion: true });
            captureMessage('Using slow motion.');
        }

        this.videoElementHighRef.current.playbackRate = value;
        this.videoElementLowRef.current.playbackRate = value;
    }
}
